import { template } from "@ember/template-compiler";
import { CLINICIAN_SEARCH_TIME_BUCKET_ORDER, CLINICIAN_SEARCH_WEEKDAY_ORDER, clinicianSearchTimeBucketLabelKey, clinicianSearchWeekdayLabelKey, facetOptionDisabled } from 'client-portal/utils/clinicianSearch/facet-catalog';
import { fn } from '@ember/helper';
import { parseCommaSeparatedInts } from 'client-portal/utils/clinicianSearch/comma-query-param';
import { service } from '@ember/service';
import { toggleSelection } from 'client-portal/utils/clinicianSearch/toggle-selection';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import RequestClinicianSearchFiltersFilterCheckboxLine from '../filters/filter-checkbox-line';
import RequestClinicianSearchFiltersPanel from '../filters/panel';
import styles from './availability.module.scss';
import t from 'ember-intl/helpers/t';
export default class RequestClinicianSearchFacetFiltersAvailability extends Component {
    @service
    intl;
    @service
    clinicianSearch;
    styles = styles;
    @tracked
    selectedWeekdays = [];
    @tracked
    selectedTimeBuckets = [];
    @tracked
    weekdayCountById = new Map();
    @tracked
    timeCountById = new Map();
    constructor(){
        super(...arguments);
        this.selectedWeekdays = parseCommaSeparatedInts(this.clinicianSearch.query.availableDays);
        this.selectedTimeBuckets = parseCommaSeparatedInts(this.clinicianSearch.query.availableTimes);
    }
    get filterOptionsLoadingAriaLabel() {
        return this.intl.t('booking.clinicianSearch.filterDetail.filterOptionsLoading');
    }
    get weekdaysGroupLabel() {
        return this.intl.t('booking.clinicianSearch.filterDetail.weekdaysHeading');
    }
    get timesGroupLabel() {
        return this.intl.t('booking.clinicianSearch.filterDetail.timesHeading');
    }
    checkboxAriaLabel = (option1, groupLabel1)=>(option1 ? `${option1}, ${groupLabel1}` : '');
    get weekdayRows() {
        return CLINICIAN_SEARCH_WEEKDAY_ORDER.map((weekdayId1)=>({
                weekdayId: weekdayId1,
                docCount: this.weekdayCountById.get(weekdayId1) ?? 0
            }));
    }
    get timeRows() {
        return CLINICIAN_SEARCH_TIME_BUCKET_ORDER.map((timeBucketId1)=>({
                timeBucketId: timeBucketId1,
                docCount: this.timeCountById.get(timeBucketId1) ?? 0
            }));
    }
    isWeekdaySelected = (weekdayId1)=>this.selectedWeekdays.includes(weekdayId1);
    isTimeSelected = (timeBucketId1)=>this.selectedTimeBuckets.includes(timeBucketId1);
    refreshFacets = async ()=>{
        let baseRouteParams1 = this.clinicianSearch.query.toRouteParams();
        let weekdayMeta1 = this.clinicianSearch.sendAll.buildFacetsMeta({
            ...baseRouteParams1,
            availableDays: null
        });
        let timeMeta1 = this.clinicianSearch.sendAll.buildFacetsMeta({
            ...baseRouteParams1,
            availableTimes: null
        });
        this.weekdayCountById = new Map((weekdayMeta1?.facets?.availableDays ?? []).map((bucket1)=>[
                Number(bucket1.key),
                bucket1.count
            ]));
        this.timeCountById = new Map((timeMeta1?.facets?.availableTimes ?? []).map((bucket1)=>[
                Number(bucket1.key),
                bucket1.count
            ]));
    };
    weekdayLabel = (weekdayId1)=>{
        let key1 = clinicianSearchWeekdayLabelKey(weekdayId1);
        return key1 ? this.intl.t(key1) : '';
    };
    timeLabel = (timeBucketId1)=>{
        let key1 = clinicianSearchTimeBucketLabelKey(timeBucketId1);
        return key1 ? this.intl.t(key1) : '';
    };
    weekdayOptionDisabled = (bucket1)=>facetOptionDisabled(bucket1.docCount, this.isWeekdaySelected(bucket1.weekdayId));
    timeOptionDisabled = (bucket1)=>facetOptionDisabled(bucket1.docCount, this.isTimeSelected(bucket1.timeBucketId));
    disabledRowTitle = (isDisabled1)=>isDisabled1 ? this.intl.t('booking.clinicianSearch.filterDetail.disabledFilterOption') : '';
    updateSelection = (key1, value1, checked1)=>{
        this[key1] = toggleSelection(this[key1], value1, checked1);
    };
    toggleWeekday = (weekdayId1, event1)=>this.updateSelection('selectedWeekdays', weekdayId1, event1.target.checked);
    toggleTime = (timeBucketId1, event1)=>this.updateSelection('selectedTimeBuckets', timeBucketId1, event1.target.checked);
    apply = ()=>{
        this.clinicianSearch.query.applyFacetFilter('availability', this.selectedWeekdays, this.selectedTimeBuckets);
        this.clinicianSearch.mobile.backFromDetail();
    };
    static{
        template(`
    <RequestClinicianSearchFiltersPanel
      @class={{this.styles.component}}
      @facetKey="availability"
      @isLoading={{this.clinicianSearch.sendAll.isLoading}}
      @loadingAriaLabel={{this.filterOptionsLoadingAriaLabel}}
      @onApply={{this.apply}}
      @onLoad={{this.refreshFacets}}
      data-test-clinician-search-filter-availability-options
    >
      <p class="filter-section-title">{{t
          "booking.clinicianSearch.filterDetail.availabilityPanelTitle"
        }}</p>
      <div class="body">
        <div
          class="filter-field-block"
          role="group"
          aria-labelledby="clinician-search-availability-weekdays-heading"
        >
          <p
            class="subsection-title visually-hidden"
            id="clinician-search-availability-weekdays-heading"
          >
            {{t "booking.clinicianSearch.filterDetail.weekdaysHeading"}}
          </p>
          <ul class="filter-checkbox-list" role="list">
            {{#each this.weekdayRows as |bucket|}}
              <RequestClinicianSearchFiltersFilterCheckboxLine
                @kind="weekday"
                @optionId={{bucket.weekdayId}}
                @ariaLabel={{this.checkboxAriaLabel
                  (this.weekdayLabel bucket.weekdayId)
                  this.weekdaysGroupLabel
                }}
                @docCount={{bucket.docCount}}
                @title={{this.disabledRowTitle (this.weekdayOptionDisabled bucket)}}
                @checked={{this.isWeekdaySelected bucket.weekdayId}}
                @disabled={{this.weekdayOptionDisabled bucket}}
                @onChange={{fn this.toggleWeekday bucket.weekdayId}}
              >
                {{this.weekdayLabel bucket.weekdayId}}
              </RequestClinicianSearchFiltersFilterCheckboxLine>
            {{/each}}
          </ul>
        </div>

        <div
          class="filter-field-block time-stack"
          role="group"
          aria-labelledby="clinician-search-availability-times-heading"
        >
          <p
            class="subsection-title visually-hidden"
            id="clinician-search-availability-times-heading"
          >
            {{t "booking.clinicianSearch.filterDetail.timesHeading"}}
          </p>
          <ul class="filter-checkbox-list" role="list">
            {{#each this.timeRows as |bucket|}}
              <RequestClinicianSearchFiltersFilterCheckboxLine
                @kind="time"
                @optionId={{bucket.timeBucketId}}
                @ariaLabel={{this.checkboxAriaLabel
                  (this.timeLabel bucket.timeBucketId)
                  this.timesGroupLabel
                }}
                @docCount={{bucket.docCount}}
                @title={{this.disabledRowTitle (this.timeOptionDisabled bucket)}}
                @checked={{this.isTimeSelected bucket.timeBucketId}}
                @disabled={{this.timeOptionDisabled bucket}}
                @onChange={{fn this.toggleTime bucket.timeBucketId}}
              >
                {{this.timeLabel bucket.timeBucketId}}
              </RequestClinicianSearchFiltersFilterCheckboxLine>
            {{/each}}
          </ul>
        </div>
      </div>
    </RequestClinicianSearchFiltersPanel>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
